import {
  Box,
  Button,
  Card,
  Group,
  List,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconAlertTriangle, IconPhone, IconSparkles } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import { propertyHooks } from '$src/lib/trpcHooks'

interface EmergencyWaterDamageCardProps {
  isPremium: boolean
}

const EMERGENCY_PHONE = '020 7484 00'
const EMERGENCY_PHONE_LINK = 'tel:+358207484000'

export function EmergencyWaterDamageCard({
  isPremium,
}: EmergencyWaterDamageCardProps) {
  const navigate = useNavigate()
  const { data: propertiesResponse } = propertyHooks.useList()

  const openEmergencyModal = () => {
    modals.open({
      title: 'Vuotovahingon hätäapu',
      centered: true,
      size: 'lg',
      children: (
        <Stack gap="lg">
          <Stack gap="xs">
            <Text size="lg" fw={700}>
              1. Sulje asuntosi vesisulku
            </Text>
            <Text size="sm">
              Asunnon vesisulku löytyy useimmiten asuntosi teknisestä tilasta
              päävesimittarin yhteydestä.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="lg" fw={700}>
              2. Soita hätäpäivystykseen numeroon 020 7484 00
            </Text>
            <Text size="sm">
              Puhelu ohjautuu yhteistyökumppanimme Polygonin päivystykseen, joka
              on auki aina (24/7). Vuotovahinkojen lisäksi voit ilmoittaa
              hätäpäivystykseen pienistä palovahingoista, esim. kun savua on
              päässyt asuntoon.
            </Text>
            <Button
              component="a"
              href={EMERGENCY_PHONE_LINK}
              size="lg"
              color="red"
              variant="filled"
              fullWidth
              leftSection={<IconPhone size={20} />}
            >
              {EMERGENCY_PHONE}
            </Button>
          </Stack>

          <Stack gap="xs">
            <Text size="lg" fw={700}>
              3. Mainitse puhelun alussa olevasi Markku.fi asiakas. Kerro, mitä
              on tapahtunut ja noudata sinulle annettavia ohjeita.
            </Text>
            <Text size="sm">
              Asiakassuhde syntyy puhelussa Sinun ja Polygonin välille. He
              laskuttavat Sinua suoraan hätätilannekäynnistä ja jälkivahinkojen
              torjunnasta. Olemme neuvotelleet Markku asiakkaille alla näkyvät
              alennetut hinnat Polygonin palveluihin.
            </Text>
            <Text size="sm" fw={600} mt="xs">
              Markku asiakkaiden hinnat (sis. ALV 25,5 %):
            </Text>
            <Table withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Palvelu</Table.Th>
                  <Table.Th>Hinta</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td>Jälkivahingontorjunta (JVT)</Table.Td>
                  <Table.Td>68,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Erikoispuhdistus</Table.Td>
                  <Table.Td>68,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Kosteus-/palovahinkokartoitus</Table.Td>
                  <Table.Td>76,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Työnjohto</Table.Td>
                  <Table.Td>80,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>Sisäilma-asiantuntija</Table.Td>
                  <Table.Td>94,00 € / h</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={600}>Hälytyskorvaus</Table.Td>
                  <Table.Td fw={600}>135,00 € / kpl</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
            <Text size="xs" c="dimmed" mt="xs">
              Lisäksi veloitetaan: kilometrikorvaus 1,65 € / km
              (henkilö-/pakettiauto), 2,40 € / km (kuorma-auto), materiaalit
              Polygonin listahinta -25 % + materiaalilisä 12 %, LVIS-työt ja
              alihankinnat +12 %.
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              Ylityöt ja lisäkorvaukset: Ylityöt veloitetaan toteuman mukaan
              Teknologiateollisuuden työntekijöiden työehtosopimuksen mukaisilla
              prosenttikorotuksilla. Aattokorvaus +100 % (edellinen arki klo
              18.00 – seuraava vuorokausi klo 06.00), Pyhätyö +100 % (edellinen
              arki/lauantai klo 18.00 – seuraava arki klo 06.00).
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              Huom: Jos työ korvataan vakuutusyhtiön toimesta, sovelletaan
              Polygonin ja vakuutusyhtiön välisen sopimuksen mukaisia hintoja.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="lg" fw={700}>
              4. Tee vahinkoilmoitus vakuutusyhtiöllesi
            </Text>
            <Text size="sm">
              Useissa tapauksissa vakuutusyhtiö korvaa jälkivahinkojen
              torjunnasta Sinulle aiheutuneet kustannukset, mutta jokainen
              tapaus käsitellään erikseen.
            </Text>
          </Stack>

          <Stack gap="xs">
            <Text size="lg" fw={700}>
              5. Lisää Polygonilta tuleva vahinkoraportti Markku-huoltokirjaasi.
            </Text>
          </Stack>
        </Stack>
      ),
    })
  }

  const openSubscriptionModal = () => {
    const firstProperty = propertiesResponse?.data[0]

    modals.open({
      title: 'Vuotovahingon hätäapu',
      centered: true,
      children: (
        <Stack gap="lg">
          <Text>
            Vuotovahingot eivät valitse aikaa. Me olemme paikalla, kun
            tarvitset.
          </Text>

          <List
            spacing="xs"
            size="sm"
            icon={
              <IconSparkles
                size={16}
                color="var(--mantine-color-brandOrange-6)"
              />
            }
          >
            <List.Item>Päivystys 24/7 - myös jouluna ja juhannuksena</List.Item>
            <List.Item>Ammattilaiset paikalle nopeasti</List.Item>
            {/*<List.Item>Yhteistyössä vakuutusyhtiöiden kanssa</List.Item>*/}
            <List.Item>
              Vuotovahinkopäivystys sisältyy kaikkiin Markku-paketteihin
            </List.Item>
          </List>

          <Button
            fullWidth
            color="brandOrange"
            onClick={() => {
              modals.closeAll()
              if (firstProperty) {
                navigate({
                  to: '/property/$propertyId',
                  params: { propertyId: String(firstProperty.id) },
                  search: { tab: 'package' },
                })
              }
            }}
          >
            Tutustu Markku-paketteihin
          </Button>
        </Stack>
      ),
    })
  }

  // TODO: disabled for now, because navigating to propert/$propertyId doesn't work if user doesn't have property
  // which is the case when app is opened first time
  if (!isPremium) {
    return null
  }

  return (
    <Card
      radius="md"
      withBorder
      p="sm"
      bg={isPremium ? undefined : 'orange.0'}
      onClick={isPremium ? openEmergencyModal : openSubscriptionModal}
      style={{
        cursor: 'pointer',
        transition: 'all 0.2s',
        opacity: isPremium ? 1 : 0.85,
        position: 'relative',
      }}
      styles={{
        root: {
          '&:hover': {
            transform: 'translateX(4px)',
            borderColor: isPremium
              ? 'var(--mantine-color-red-3)'
              : 'var(--mantine-color-brandOrange-4)',
          },
        },
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Box
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--mantine-color-red-0)',
              flexShrink: 0,
            }}
          >
            <IconAlertTriangle size={20} color="var(--mantine-color-red-7)" />
          </Box>
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text fw={600}>Vuotovahingon hätäapu 24/7</Text>
            <Text c="dimmed" size="sm">
              {isPremium
                ? 'Päivystys ympäri vuorokauden'
                : 'Mielenrauhaa ympäri vuorokauden'}
            </Text>
          </Stack>
        </Group>
        <Box style={{ flexShrink: 0 }}>
          {isPremium ? (
            <IconPhone size={18} color="var(--mantine-color-gray-5)" />
          ) : (
            <IconSparkles
              size={18}
              color="var(--mantine-color-brandOrange-6)"
            />
          )}
        </Box>
      </Group>
    </Card>
  )
}
